import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock, faCheckCircle, faTimesCircle,
  faWallet, faArrowLeft, faPlus, faUniversity, faReceipt, faCoins,
  faRotate, faDownload, faInbox,
} from '@fortawesome/free-solid-svg-icons';
import { bankList } from '../utils/bankList';
import DataTable, { Column } from '../components/shared/DataTable';
import ActionOtpModal from '../components/shared/ActionOtpModal';
import {
  UiPageHeader, UiFilterPanel, UiButton, UiModal, UiInput, UiSelect, StatusBadge, FIELD_INPUT_CLASS, FIELD_LABEL_CLASS,
} from '../components/ui';
import type { StatusDef } from '../components/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = 'Asia/Bangkok';

const SettlementPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { merchant } = useAuth();
  const isEmployee = merchant?.type === 'employee';
  const canView = !isEmployee ||
    merchant?.permissions?.can_view_settlement === true ||
    merchant?.permissions?.can_create_settlement === true;
  const canCreate = !isEmployee || merchant?.permissions?.can_create_settlement === true;

  const [wallet, setWallet] = useState<any>(null);
  const [settlements, setSettlements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [minAmount, setMinAmount] = useState(100);
  const [currency, setCurrency] = useState<'THB' | 'USDT'>('THB');
  // เปิด/ปิดถอนแยกตามสกุลเงิน (default เปิด ถ้าไม่พบ setting)
  const [thbEnabled, setThbEnabled] = useState(true);
  const [usdtEnabled, setUsdtEnabled] = useState(true);
  const [formData, setFormData] = useState({
    amount: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_name: '',
    wallet_address: '',
    network: '',
    note: '',
  });
  const [usdtPreview, setUsdtPreview] = useState<{
    rate: number; fee_amount: number; net_thb: number;
    usdt_amount: number; fee_percent: number; original_amount: number;
  } | null>(null);
  const [usdtLoading, setUsdtLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [selectedSlipUrl, setSelectedSlipUrl] = useState('');

  // Filter state
  const today = dayjs().tz(TZ).format('YYYY-MM-DD');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedDateFrom, setAppliedDateFrom] = useState(today);
  const [appliedDateTo, setAppliedDateTo] = useState(today);
  const [statusFilter, setStatusFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [settlementsLoading, setSettlementsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const STATUS_MAP: Record<string, StatusDef> = {
    pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faClock, label: t('settlement.pending') },
    approved:  { bg: 'bg-blue-100', text: 'text-blue-700', icon: faCheckCircle, label: t('settlement.approved') },
    completed: { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: t('settlement.completed') },
    rejected:  { bg: 'bg-red-100', text: 'text-red-600', icon: faTimesCircle, label: t('settlement.rejected') },
  };

  const loadWallet = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/wallet');
      setWallet(response.data.data);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadSettlements = useCallback(async () => {
    setSettlementsLoading(true);
    try {
      const offset = (currentPage - 1) * pageSize;
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(offset),
      });
      if (appliedDateFrom) params.set('date_from', appliedDateFrom);
      if (appliedDateTo) params.set('date_to', appliedDateTo);
      if (statusFilter) params.set('status', statusFilter);
      if (currencyFilter) params.set('currency', currencyFilter);

      const response = await merchantApi.get(`/merchants/wallet/settlements?${params}`);
      setSettlements(response.data.data || []);
      setTotalItems(response.data.pagination?.total || 0);
    } catch {
      console.error('Error loading settlements');
    } finally {
      setSettlementsLoading(false);
    }
  }, [currentPage, pageSize, appliedDateFrom, appliedDateTo, statusFilter, currencyFilter]);

  const loadSettings = useCallback(async () => {
    try {
      const response = await merchantApi.get('/admin/settings');
      const settings = response.data.data || [];
      const minSetting = settings.find((s: any) => s.setting_key === 'min_settlement_amount');
      if (minSetting) setMinAmount(parseFloat(minSetting.setting_value) || 100);
      const thbSetting = settings.find((s: any) => s.setting_key === 'settlement_enabled');
      const usdtSetting = settings.find((s: any) => s.setting_key === 'settlement_enabled_usdt');
      setThbEnabled(!thbSetting || thbSetting.setting_value !== 'false');
      setUsdtEnabled(!usdtSetting || usdtSetting.setting_value !== 'false');
    } catch {
      console.error('Error loading settings');
    }
  }, []);

  useEffect(() => {
    loadWallet();
    loadSettings();
  }, [loadWallet, loadSettings]);

  useEffect(() => {
    loadSettlements();
  }, [loadSettlements]);

  // ถ้าสกุลที่เลือกอยู่ถูกปิด ให้สลับไปสกุลที่ยังเปิด (ถ้าปิดทั้งคู่ปล่อยค้างไว้ แล้ว disable submit)
  useEffect(() => {
    if (currency === 'THB' && !thbEnabled && usdtEnabled) setCurrency('USDT');
    else if (currency === 'USDT' && !usdtEnabled && thbEnabled) setCurrency('THB');
  }, [currency, thbEnabled, usdtEnabled]);

  useEffect(() => {
    if (currency !== 'USDT' || !formData.amount || parseFloat(formData.amount) <= 0) {
      setUsdtPreview(null);
      return;
    }
    const timer = setTimeout(async () => {
      setUsdtLoading(true);
      try {
        const res = await merchantApi.get(`/merchants/wallet/usdt-rate?amount=${formData.amount}`);
        setUsdtPreview(res.data.data.preview || null);
      } catch {
        setUsdtPreview(null);
      } finally {
        setUsdtLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [currency, formData.amount]);

  const handleRefreshRate = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) return;
    setUsdtLoading(true);
    try {
      const res = await merchantApi.get(`/merchants/wallet/usdt-rate?amount=${formData.amount}`);
      setUsdtPreview(res.data.data.preview || null);
    } catch {
      setUsdtPreview(null);
    } finally {
      setUsdtLoading(false);
    }
  };

  const formatSlipUrl = (slipUrl: string) => {
    if (!slipUrl) return '';
    if (!slipUrl.startsWith('http') && !slipUrl.startsWith('data:'))
      return `data:image/png;base64,${slipUrl}`;
    return slipUrl;
  };

  const handleViewSlip = (slipUrl: string) => {
    setSelectedSlipUrl(formatSlipUrl(slipUrl));
    setShowSlipModal(true);
  };

  // Amount validation — applies to BOTH THB and USDT (amount is always entered in baht)
  const amountNum = parseFloat(formData.amount);
  const balance = wallet?.balance || 0;
  const isBelowMin = !isNaN(amountNum) && amountNum < minAmount;
  const isAboveBalance = !isNaN(amountNum) && amountNum > balance;
  const isAmountValid = !isNaN(amountNum) && amountNum >= minAmount && amountNum <= balance;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((currency === 'THB' && !thbEnabled) || (currency === 'USDT' && !usdtEnabled)) {
      toast.error(currency === 'USDT'
        ? t('settlement.usdtDisabled')
        : t('settlement.thbDisabled'));
      return;
    }
    if (!isAmountValid) {
      toast.error(isBelowMin
        ? t('settlement.belowMinWithdraw', { min: minAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 }) })
        : t('settlement.exceedsBalance'));
      return;
    }
    const payload: any = { amount: parseFloat(formData.amount), currency, note: formData.note };
    if (currency === 'THB') {
      payload.bank_account_name = formData.bank_account_name;
      payload.bank_account_number = formData.bank_account_number;
      payload.bank_name = formData.bank_name;
    } else {
      payload.wallet_address = formData.wallet_address;
      payload.network = formData.network;
    }
    setPendingPayload(payload);
    setShowOtpModal(true);
  };

  const handleOtpVerified = async (actionToken: string) => {
    try {
      await merchantApi.post('/merchants/wallet/settlement', {
        ...pendingPayload,
        action_token: actionToken,
      });
      toast.success(t('settlement.withdrawSuccess'));
      setShowOtpModal(false);
      setShowRequestModal(false);
      setPendingPayload(null);
      setCurrency('THB');
      setFormData({ amount: '', bank_account_name: '', bank_account_number: '', bank_name: '', wallet_address: '', network: '', note: '' });
      loadWallet();
      loadSettlements();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'));
    }
  };

  const handleDateSearch = () => {
    if (dateFrom && dateTo && dayjs(dateFrom).isAfter(dayjs(dateTo))) {
      toast.error(t('order.dateFromError'));
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setStatusFilter('');
    setCurrencyFilter('');
    setCurrentPage(1);
  };

  const exportCSV = async () => {
    if (totalItems === 0) {
      toast.warning(t('settlement.noDataExport'));
      return;
    }
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit: '10000', offset: '0' });
      if (appliedDateFrom) params.set('date_from', appliedDateFrom);
      if (appliedDateTo) params.set('date_to', appliedDateTo);
      if (statusFilter) params.set('status', statusFilter);
      if (currencyFilter) params.set('currency', currencyFilter);

      const response = await merchantApi.get(`/merchants/wallet/settlements?${params}`);
      const rows: any[] = response.data.data || [];

      if (rows.length === 0) {
        toast.warning(t('settlement.noDataExport'));
        return;
      }

      const headers = [
        t('common.date'), t('settlement.time'), t('common.status'), t('settlement.type'), t('settlement.amountThb'),
        t('settlement.feeAmount'), t('common.accountName'), t('common.bankAccount'), t('common.bank'),
        'Network', t('settlement.walletAddressLabel'), t('settlement.usdtAmount'), t('settlement.exchangeRateLabel'), t('common.note'),
      ];

      const escape = (v: any) => {
        const s = v == null ? '' : String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const lines = [
        headers.join(','),
        ...rows.map(s => [
          escape(dayjs(s.created_at).tz(TZ).format('DD/MM/YYYY')),
          escape(dayjs(s.created_at).tz(TZ).format('HH:mm:ss')),
          escape(s.status),
          escape(s.currency || 'THB'),
          escape(parseFloat(s.amount).toFixed(2)),
          escape(s.fee_amount ? parseFloat(s.fee_amount).toFixed(2) : '0'),
          escape(s.bank_account_name || ''),
          escape(s.bank_account_number || ''),
          escape(s.bank_name || ''),
          escape(s.network || ''),
          escape(s.wallet_address || ''),
          escape(s.usdt_amount ? parseFloat(s.usdt_amount).toFixed(6) : ''),
          escape(s.usdt_rate ? parseFloat(s.usdt_rate).toFixed(2) : ''),
          escape(s.note || ''),
        ].join(',')),
      ];

      const bom = '﻿';
      const blob = new Blob([bom + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const suffix = `${appliedDateFrom}_${appliedDateTo}`;
      link.download = `settlements_${suffix}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(t('settlement.exportSuccess', { count: rows.length }));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setExporting(false);
    }
  };

  // DataTable columns
  const columns: Column<any>[] = [
    {
      key: 'created_at',
      header: t('common.date'),
      sortable: true,
      render: (s) => (
        <div>
          <div className="text-sm font-semibold text-gray-700 dark:text-slate-200">
            {dayjs(s.created_at).tz(TZ).format('DD/MM/YYYY')}
          </div>
          <div className="text-xs mt-0.5 text-gray-400 dark:text-slate-500">
            {dayjs(s.created_at).tz(TZ).format('HH:mm')}
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('settlement.amountThb'),
      sortable: true,
      render: (s) => (
        <div>
          <div className="text-lg font-bold" style={{ color: 'var(--theme-accent)', fontVariantNumeric: 'tabular-nums' }}>
            ฿{parseFloat(s.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </div>
          {s.fee_amount && parseFloat(s.fee_amount) > 0 && (
            <div className="text-xs text-red-500 dark:text-red-400">
              {t('settlement.feeAmount')}: -฿{parseFloat(s.fee_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'currency',
      header: t('settlement.type'),
      render: (s) => {
        const cur = s.currency || 'THB';
        return cur === 'USDT' ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
            <FontAwesomeIcon icon={faCoins} /> USDT
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
            <FontAwesomeIcon icon={faUniversity} /> THB
          </span>
        );
      },
    },
    {
      key: 'destination',
      header: t('settlement.destination'),
      render: (s) => {
        if ((s.currency || 'THB') === 'USDT') {
          return (
            <div className="min-w-0">
              <div className="text-xs font-semibold text-green-600 dark:text-green-400">{s.network || '-'}</div>
              <div className="text-xs mt-0.5 truncate max-w-[160px] font-mono text-gray-400 dark:text-slate-500" title={s.wallet_address}>
                {s.wallet_address || '-'}
              </div>
            </div>
          );
        }
        return (
          <div>
            <div className="text-sm font-medium text-gray-800 dark:text-slate-100">{s.bank_account_name || '-'}</div>
            <div className="text-xs mt-0.5 text-gray-400 dark:text-slate-500">
              {s.bank_name} {s.bank_account_number ? `• ${s.bank_account_number}` : ''}
            </div>
          </div>
        );
      },
    },
    {
      key: 'usdt_amount',
      header: 'USDT',
      render: (s) => {
        if (!s.usdt_amount) return <span className="text-gray-400 dark:text-slate-500">-</span>;
        return (
          <div>
            <div className="font-bold text-sm text-green-600 dark:text-green-400">
              {parseFloat(s.usdt_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT
            </div>
            <div className="text-xs text-gray-400 dark:text-slate-500">
              @฿{parseFloat(s.usdt_rate || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
            </div>
          </div>
        );
      },
    },
    {
      key: 'note',
      header: t('settlement.note'),
      render: (s) => s.note
        ? <span className="text-xs text-gray-500 dark:text-slate-400">{s.note}</span>
        : <span className="text-gray-400 dark:text-slate-500">-</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      render: (s) => <StatusBadge status={s.status} statusMap={STATUS_MAP} />,
    },
    {
      key: 'actions',
      header: '',
      render: (s) => s.slip_url ? (
        <button
          onClick={(e) => { e.stopPropagation(); handleViewSlip(s.slip_url); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50"
        >
          <FontAwesomeIcon icon={faReceipt} /> {t('settlement.viewSlip')}
        </button>
      ) : null,
    },
  ];

  // Summary footer row
  const totalThb = settlements.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
  const totalUsdt = settlements
    .filter(s => s.usdt_amount)
    .reduce((sum, s) => sum + parseFloat(s.usdt_amount || 0), 0);

  const tableFooter = (
    <tr>
      <td className="px-4 py-3">
        <strong className="text-sm text-gray-600 dark:text-slate-300">
          {t('common.total')} {settlements.length} {t('common.items')}
        </strong>
      </td>
      <td className="px-4 py-3">
        <strong style={{ color: 'var(--theme-accent)' }}>
          ฿{totalThb.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </strong>
      </td>
      <td colSpan={3} className="px-4 py-3">
        {totalUsdt > 0 && (
          <strong className="text-sm text-green-600 dark:text-green-400">
            {totalUsdt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT
          </strong>
        )}
      </td>
      <td colSpan={3}></td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-lg font-semibold text-gray-800 dark:text-slate-100">
          {t('common.noPermission', 'ไม่มีสิทธิ์เข้าถึงหน้านี้')}
        </p>
        <UiButton icon={faArrowLeft} onClick={() => navigate('/')}>{t('common.back')}</UiButton>
      </div>
    );
  }

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faWallet}
        title={t('settlement.title')}
        subtitle={`${t('settlement.desc')} • ${settlements.length.toLocaleString()} รายการ`}
        actions={
          <UiButton icon={faArrowLeft} onClick={() => navigate('/wallet')}>
            {t('settlement.backToWallet')}
          </UiButton>
        }
      />

      {/* Filter */}
      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleDateSearch}
        onReset={handleClearFilters}
        loading={settlementsLoading}
        actions={
          <>
            <UiButton variant="secondary" icon={faRotate} onClick={loadSettlements}>
              {t('common.refresh')}
            </UiButton>
            <UiButton
              variant="secondary"
              icon={exporting ? faRotate : faDownload}
              onClick={exportCSV}
              disabled={exporting || settlements.length === 0}
            >
              {exporting ? t('settlement.exporting') : 'Export CSV'}
            </UiButton>
          </>
        }
      >
        <UiSelect
          wrapperClassName="w-fit min-w-[150px]"
          label={t('common.status')}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="">{t('settlement.allStatus')}</option>
          <option value="pending">{t('settlement.pending')}</option>
          <option value="approved">{t('settlement.approved')}</option>
          <option value="completed">{t('settlement.completed')}</option>
          <option value="rejected">{t('settlement.rejected')}</option>
        </UiSelect>
        <UiSelect
          wrapperClassName="w-fit min-w-[150px]"
          label={t('settlement.type')}
          value={currencyFilter}
          onChange={(e) => { setCurrencyFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="">{t('settlement.allCurrencies')}</option>
          <option value="THB">{t('settlement.thbCurrency')}</option>
          <option value="USDT">USDT</option>
        </UiSelect>
      </UiFilterPanel>

      {/* Balance + Request */}
      <div className="rounded-2xl p-6 mb-6 bg-white dark:bg-slate-800 shadow-theme ring-1 ring-gray-100 dark:ring-slate-700 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm mb-1 text-gray-400 dark:text-slate-500">{t('settlement.currentBalance')}</p>
          <p className="text-3xl font-bold text-gray-800 dark:text-slate-100" style={{ fontVariantNumeric: 'tabular-nums' }}>
            ฿{(wallet?.balance || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Mini stats */}
          <div className="flex items-center gap-4 text-sm mr-2">
            {(['pending', 'completed', 'rejected'] as const).map((st) => {
              const count = settlements.filter(s => s.status === st).length;
              const colors: Record<string, string> = {
                pending: 'var(--theme-warning)',
                completed: 'var(--theme-success)',
                rejected: 'var(--theme-error)',
              };
              return (
                <button
                  key={st}
                  onClick={() => { setStatusFilter(statusFilter === st ? '' : st); setCurrentPage(1); }}
                  className="flex flex-col items-center gap-0.5 transition-transform hover:-translate-y-0.5"
                >
                  <span className="text-lg font-bold" style={{ color: colors[st] }}>{count}</span>
                  <span className="text-xs text-gray-400 dark:text-slate-500">{t(`settlement.${st}`)}</span>
                </button>
              );
            })}
          </div>

          {canCreate && (
            <UiButton
              icon={faPlus}
              onClick={() => setShowRequestModal(true)}
              disabled={!wallet || wallet.balance <= 0}
            >
              {t('settlement.requestWithdraw')}
            </UiButton>
          )}
        </div>
      </div>

      {/* Settlements Table */}
      <DataTable<any>
        data={settlements}
        columns={columns}
        loading={settlementsLoading}
        emptyMessage={t('settlement.noWithdrawHistory')}
        emptyIcon={<FontAwesomeIcon icon={faInbox} className="text-6xl text-gray-300 dark:text-slate-600" />}
        keyExtractor={(s) => s.id}
        serverPagination={{
          currentPage,
          totalItems,
          pageSize,
          pageSizeOptions: [10, 20, 50, 100],
          onPageChange: (p) => setCurrentPage(p),
          onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); },
        }}
        footer={tableFooter}
        hoverable
      />

      {/* Request Modal */}
      <UiModal
        open={canCreate && showRequestModal}
        onClose={() => setShowRequestModal(false)}
        title={t('settlement.requestWithdraw')}
        icon={faWallet}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        scrollable
      >
        <div className="rounded-xl p-4 ring-1 ring-inset ring-primary/15" style={{ background: 'var(--theme-primary-light)' }}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600 dark:text-slate-300">{t('settlement.withdrawableAmount')}</span>
            <span className="font-bold" style={{ color: 'var(--theme-accent)' }}>฿{(wallet?.balance || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-slate-300">{t('settlement.minWithdraw')}</span>
            <span className="font-medium text-gray-700 dark:text-slate-200">฿{minAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* THB / USDT toggle */}
          <div>
            <label className={FIELD_LABEL_CLASS}>{t('settlement.withdrawType')}</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCurrency('THB')}
                disabled={!thbEnabled}
                title={!thbEnabled ? t('settlement.thbDisabled') : undefined}
                className={`px-4 py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 border-2 ${
                  !thbEnabled
                    ? 'border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-600 cursor-not-allowed opacity-60'
                    : currency === 'THB'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:border-gray-300 dark:hover:border-slate-500'
                }`}
              >
                <FontAwesomeIcon icon={faUniversity} />
                {t('settlement.bankAccountTHB')}
                {!thbEnabled && <span className="text-[10px] font-normal">({t('settlement.temporarilyClosed')})</span>}
              </button>
              <button
                type="button"
                onClick={() => setCurrency('USDT')}
                disabled={!usdtEnabled}
                title={!usdtEnabled ? t('settlement.usdtDisabled') : undefined}
                className={`px-4 py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 border-2 ${
                  !usdtEnabled
                    ? 'border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-600 cursor-not-allowed opacity-60'
                    : currency === 'USDT'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:border-gray-300 dark:hover:border-slate-500'
                }`}
              >
                <FontAwesomeIcon icon={faCoins} />
                USDT
                {!usdtEnabled && <span className="text-[10px] font-normal">({t('settlement.temporarilyClosed')})</span>}
              </button>
            </div>
            {!thbEnabled && !usdtEnabled && (
              <p className="mt-2 text-xs text-red-500 dark:text-red-400">{t('settlement.allDisabled')}</p>
            )}
          </div>

          <UiInput
            label={t('settlement.withdrawAmountBaht')}
            prefix="฿"
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            step="0.01"
            min={minAmount}
            max={wallet?.balance || 0}
            required
            placeholder="0.00"
            className="text-lg font-semibold"
          />

          {formData.amount !== '' && isBelowMin && (
            <p className="-mt-2 text-xs text-red-500 dark:text-red-400">
              {t('settlement.belowMinWithdraw', { min: minAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 }) })}
            </p>
          )}
          {formData.amount !== '' && isAboveBalance && (
            <p className="-mt-2 text-xs text-red-500 dark:text-red-400">
              {t('settlement.exceedsBalance')}
            </p>
          )}

          {currency === 'USDT' && (
            <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-green-700 dark:text-green-400 flex items-center gap-1">
                  <FontAwesomeIcon icon={faCoins} /> {t('settlement.usdtSummary')}
                </p>
                <button
                  type="button"
                  onClick={handleRefreshRate}
                  disabled={usdtLoading || !formData.amount || parseFloat(formData.amount) <= 0}
                  className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 disabled:opacity-40 transition-colors"
                >
                  <FontAwesomeIcon icon={faRotate} className={usdtLoading ? 'animate-spin' : ''} />
                  {t('settlement.refreshRate')}
                </button>
              </div>
              {usdtLoading ? (
                <p className="text-xs text-gray-400 dark:text-slate-500">{t('settlement.loadingRate')}</p>
              ) : usdtPreview ? (
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-slate-400">{t('settlement.originalAmount')}</span>
                    <span className="font-medium text-gray-800 dark:text-slate-100">฿{usdtPreview.original_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-red-600 dark:text-red-400">
                    <span>{t('settlement.fee', { pct: usdtPreview.fee_percent })}</span>
                    <span className="font-medium">-฿{usdtPreview.fee_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-t border-green-200 dark:border-green-800 pt-1.5">
                    <span className="text-gray-600 dark:text-slate-300">{t('settlement.netAmountThb')}</span>
                    <span className="font-semibold text-gray-800 dark:text-slate-100">฿{usdtPreview.net_thb.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 dark:text-slate-500">
                    <span>{t('settlement.exchangeRate')}</span>
                    <span>1 USDT = ฿{usdtPreview.rate.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between mt-1 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border border-green-300 dark:border-green-800">
                    <span className="font-semibold text-green-700 dark:text-green-400">{t('settlement.youWillReceive')}</span>
                    <span className="font-bold text-green-700 dark:text-green-400 text-base">{usdtPreview.usdt_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 dark:text-slate-500">{t('settlement.enterAmountToPreview')}</p>
              )}
            </div>
          )}

          {currency === 'THB' ? (
            <>
              <UiInput
                label={`${t('common.accountName')} *`}
                type="text"
                value={formData.bank_account_name}
                onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })}
                required
                placeholder={t('settlement.accountNamePlaceholder')}
              />
              <UiInput
                label={`${t('common.bankAccount')} *`}
                type="text"
                value={formData.bank_account_number}
                onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                required
                placeholder="1234567890"
              />
              <UiSelect
                label={`${t('common.bank')} *`}
                icon={faUniversity}
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                required
              >
                <option value="">{t('common.selectBank')}</option>
                {bankList.map((bank) => (
                  <option key={bank.shortName} value={bank.shortName}>
                    {bank.bankName} ({bank.shortName})
                  </option>
                ))}
              </UiSelect>
            </>
          ) : (
            <>
              <UiSelect
                label="Network *"
                icon={faCoins}
                value={formData.network}
                onChange={(e) => setFormData({ ...formData, network: e.target.value })}
                required
              >
                <option value="">{t('settlement.selectNetwork')}</option>
                <option value="TRC20">TRC20 (Tron)</option>
                <option value="ERC20">ERC20 (Ethereum)</option>
                <option value="BEP20">BEP20 (BSC)</option>
              </UiSelect>
              <UiInput
                label={`${t('settlement.walletAddressLabel')} *`}
                type="text"
                value={formData.wallet_address}
                onChange={(e) => setFormData({ ...formData, wallet_address: e.target.value })}
                required
                placeholder={t('settlement.walletAddressPlaceholder')}
                className="font-mono text-sm"
              />
            </>
          )}

          <div>
            <label className={FIELD_LABEL_CLASS}>{t('settlement.note')}</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              rows={3}
              placeholder={t('settlement.notePlaceholder')}
              className={`${FIELD_INPUT_CLASS} resize-none`}
            />
          </div>

          <UiButton
            type="submit"
            className="w-full justify-center"
            disabled={!isAmountValid || (currency === 'THB' && !thbEnabled) || (currency === 'USDT' && !usdtEnabled)}
          >
            {t('settlement.submitRequest')}
          </UiButton>
        </form>
      </UiModal>

      <ActionOtpModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onVerified={handleOtpVerified}
        action="settlement_create"
        title={t('settlement.confirmWithdraw')}
        description={`ยืนยันการถอนเงิน ${pendingPayload?.amount?.toLocaleString()} ${pendingPayload?.currency || 'THB'}`}
      />

      {/* Slip Modal */}
      <UiModal
        open={showSlipModal}
        onClose={() => setShowSlipModal(false)}
        title={t('settlement.transferSlip')}
        icon={faReceipt}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
      >
        <div className="text-center">
          {selectedSlipUrl ? (
            <img
              src={selectedSlipUrl}
              alt="Slip"
              className="max-w-full max-h-[70vh] rounded-lg border border-gray-200 dark:border-slate-700 mx-auto"
              onError={() => toast.error(t('settlement.cannotLoadSlip'))}
            />
          ) : (
            <p className="text-gray-500 dark:text-slate-400">{t('settlement.noSlipData')}</p>
          )}
        </div>
      </UiModal>
    </div>
  );
};

export default SettlementPage;
